import { Component, Input, SimpleChanges, Output, EventEmitter, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BbFilterConfiguration } from '../../../components/shared/bb-filters/bb-filters.component';
import { TranslateService } from '@ngx-translate/core';
import { BbTableService, TableRow, BbTableSelectOption } from '../../../services/bb-table.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Playlist } from '../../../interfaces/playlist';
import { AuthenticationService } from '../../../services/authentication.service';
import { CustomField } from '../../../interfaces/publication';
import { FilterSetService } from '../../../services/filter-set.service';
import { OverlayService, ModalDefinition } from '../../../services/overlay.service';
import { PublicationService } from '../../../services/publication.service';
import { Mediaclip } from 'app/interfaces/mediaclip';
import { MediaclipService } from './../../../services/mediaclip.service';
import { bbGenerateShortId } from 'app/shared/utils/bbGenerateShortId.util';
import { Project } from 'app/interfaces/project';

@Component({
	selector: 'playlist-details-general',
	templateUrl: './playlist-details-general.component.html',
	styleUrls: ['./playlist-details-general.component.scss'],
	providers: [BbTableService]
})

export class PlaylistDetailsGeneralComponent implements OnInit, OnChanges, OnDestroy {
	@Input() playlist: Playlist;
	@Input() form: NgForm;
	@Input() queryFromTableService: string;

	@Output() onPlaylistFilterChange: EventEmitter<Playlist> = new EventEmitter<Playlist>();
	@Output() onPlaylistQueryChange: EventEmitter<Playlist> = new EventEmitter<Playlist>();
	@Output() sortOrderChange: EventEmitter<any> = new EventEmitter<any>();

	@Output() addedSelectedClips: EventEmitter<any> = new EventEmitter<any>();

	limitChanged: Subject<number>;
	filterChanged: Subject<string>;
	queryChanged: Subject<string>;

	filterSort: any = {};
	filterConfigDynamic: BbFilterConfiguration;
	filterConfigStatic: BbFilterConfiguration;
	sortPropertyOptions: any[];

	selectedRows: any = [];
	subscriptions: Subscription;
	rows: TableRow[] = [];

	constructor(
		protected translate: TranslateService,
		protected bbTableService: BbTableService,
		private mediaclipService: MediaclipService,
		protected authenticationService: AuthenticationService,
		protected publicationService: PublicationService,
		protected filterSetService: FilterSetService,
		protected overlayService: OverlayService
	) {
		this.subscriptions = new Subscription();

		this.limitChanged = new Subject<number>();
		this.filterChanged = new Subject<string>();
		this.queryChanged = new Subject<string>();

		this.sortPropertyOptions = [
			{ value: 'score', label: translate.instant('SCORE') },
			{ value: 'publisheddate', label: translate.instant('PUBLISHED_DATE') },
			{ value: 'createddate', label: translate.instant('CREATED_DATE') },
			{ value: 'updateddate', label: translate.instant('UPDATED_DATE') },
			{ value: 'title', label: translate.instant('TITLE') },
			{ value: 'mediatype', label: translate.instant('MEDIATYPE') },
			{ value: 'sourcetype', label: translate.instant('SOURCETYPE') },
			{ value: 'status', label: translate.instant('STATUS') },
			{ value: 'random', label: translate.instant('RANDOM') },
			{ value: 'views', label: this.translate.instant('VIEWS') }
		];

		this.filterConfigDynamic = {
			search: true,
			allowDatasource: this.playlist && this.playlist.allowDatasource === 'true',
			filters: this.mediaclipService.getFilterConfigOptions()
		};
		this.filterConfigDynamic.filters.push({
			property: 'typeSort',
			label: this.translate.instant('CONTENT_TYPE'),
			type: 'select',
		});

		this.filterConfigDynamic.filters = this.filterConfigDynamic.filters.map(filter => {
			filter.entity = 'search';
			return filter;
		});

		this.filterConfigStatic = {
			search: true,
			filters: [
				{ property: 'mediatype', label: this.translate.instant('CONTENT_TYPE'), type: 'select' },
				{ property: 'sourcetype', label: this.translate.instant('SOURCETYPE'), type: 'select' },
				{ property: 'title', label: this.translate.instant('TITLE'), type: 'text' },
				{ property: 'cat', label: this.translate.instant('TAGS'), type: 'select' },
				{ property: 'status', label: this.translate.instant('STATUS'), type: 'select' },
				{ property: 'publisheddate', label: this.translate.instant('PUBLISHED_DATE'), type: 'date' },
				{ property: 'createddate', label: this.translate.instant('CREATED_DATE'), type: 'date' },
				{ property: 'updateddate', label: this.translate.instant('UPDATED_DATE'), type: 'date' },
				{ property: 'sourceid', label: this.translate.instant('SOURCE_ID'), type: 'text' },
				{ property: 'isEmpty', label: this.translate.instant('IS_EMPTY'), type: 'boolean' }
			]
		};

		this.subscriptions.add(this.authenticationService.getAllowedCustomFields().subscribe((fields: CustomField[]) => {
			fields.forEach((field: CustomField) => {

				// map custom field type to filter type
				let filterType: string;

				switch (field.type) {
					case 'text':
						filterType = 'text';
						break;
					case 'int':
					case 'float':
						filterType = 'number';
						break;
					case 'date':
						filterType = 'date';
						break;
					case 'boolean':
						filterType = 'boolean';
						break;
					case 'string':
						filterType = 'text';
						break;
					case 'multistring':
						filterType = 'customMultiSelect';
						break;
				}

				// for now, "color" and "relation" are ignored
				if (filterType) {
					this.filterConfigDynamic.filters.push({
						property: field.name,
						label: field.label,
						type: filterType,
						entity: 'search',
					});
					this.filterConfigStatic.filters.push({
						property: field.name,
						label: field.label,
						type: filterType
					});
				}
			});
		}));

		this.filterSetService.setLinkedToUrl(false);

		// setup default search in Project and Mediaclip (static)
		this.filterSetService.setEntity('search');
		this.filterSetService.setBaseFilterSet([{
			filters: [{
				type: 'search',
				field: 'type',
				operator: 'is',
				value: ['Project', 'MediaClip', 'MediaClipList']
			}],
		}]);
	}

	ngOnInit() {
		this.subscriptions.add(this.limitChanged
			.pipe(debounceTime(400))
			.subscribe((limit) => {
				this.onQueryLimitChange(limit);
			}));

		this.subscriptions.add(this.filterChanged
			.pipe(debounceTime(400))
			.subscribe((filterSet) => {
				this.onFilterChange(filterSet);
			}));

		this.subscriptions.add(this.queryChanged
			.pipe(debounceTime(400))
			.subscribe((query) => {
				this.onQueryChange(query);
			}));

		if (this.playlist.listtype === 'static') {
			this.subscriptions.add(this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
				if (rows) {
					this.rows = rows;
				}
			}));

			this.subscriptions.add(this.bbTableService.rowsSelectedObservable.subscribe((rows: TableRow[]) => {
				this.selectedRows = rows;
			}));

			this.bbTableService
				.setEntity('search')
				.setBaseFilterSet([(
					{
						filters: [{
							type: 'search',
							field: 'type',
							operator: 'is',
							value: ['Project', 'MediaClip']
						}]
					})])
				.setConfigurable(false)
				.setSelect(BbTableSelectOption.multi)
				.setLimit(15)
				.sort('createddate', 'desc')
				.getRows();
		}

		if (typeof this.playlist.sort === 'undefined' || this.playlist.sort == null) {
			this.playlist.sort = 'publisheddate desc';
			this.filterSort = {
				property: 'publisheddate',
				direction: 'desc'
			};
		} else {
			const parts = this.playlist.sort.split(' ');

			this.filterSort = {
				property: parts[0],
				direction: parts[1]
			};
		}

		if (this.playlist && this.playlist.allowDatasource === 'true') {
			this.filterConfigDynamic.allowDatasource = true;
		}
		this.updateSort();
		this.overlayService.setLoading(false);
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.queryFromTableService && simpleChanges.queryFromTableService.currentValue) {
			if (this.playlist.filterType !== 'solr') {
				this.playlist.query = simpleChanges.queryFromTableService.currentValue;
			}
		}

		if (simpleChanges && simpleChanges.playlist && simpleChanges.playlist.currentValue) {
			if (simpleChanges.playlist.currentValue.filters && simpleChanges.playlist.currentValue.filters.length > 0) {
				this.onFilterChange(simpleChanges.playlist.currentValue.filters);
			} else if (simpleChanges.playlist.currentValue.query) {
				this.onQueryChange(simpleChanges.playlist.currentValue.query);
			}
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onSuggestsChange(useSuggests: boolean) {
		if (useSuggests) {
			this.playlist.limit = 30;
			this.onPlaylistFilterChange.emit(this.playlist);
		}
	}

	onIsSlideshowChange(isSlideshow: string) {
		const imageFilterGroup = {
			filters: [{
				id: bbGenerateShortId(),
				type: 'mediaclip',
				field: 'mediatype',
				operator: 'containsAnyOf',
				value: ['image']
			}],
			readOnly: true
		};
		if (!this.playlist.slideDuration) {
			this.playlist.slideDuration = 10;
		}
		if (this.playlist.listtype === 'dynamic') {
			if (!this.playlist.filters) {
				this.playlist.filters = [];
			}
			const mediatypeFilterGroup = this.playlist.filters.find((fg) => fg.filters[0] && fg.filters[0].field === 'mediatype');
			if (isSlideshow === 'true') {
				if (!mediatypeFilterGroup) {
					this.playlist.filters.unshift(imageFilterGroup);
				} else {
					mediatypeFilterGroup.readOnly = true;
					mediatypeFilterGroup.filters[0].value = ['image'];
				}
			} else {
				if (mediatypeFilterGroup) {
					this.playlist.filters.shift();
					mediatypeFilterGroup.readOnly = false;
				}
			}
			this.onPlaylistFilterChange.emit(this.playlist);
		} else {
			const filterSet = this.filterSetService.getFilterSet();
			const mediatypeFilterGroup = filterSet.find((fg) => fg.filters[0] && fg.filters[0].field === 'mediatype');
			if (isSlideshow === 'true') {
				if (!mediatypeFilterGroup) {
					this.filterSetService.addFilterGroup(imageFilterGroup);
				} else {
					mediatypeFilterGroup.readOnly = true;
					mediatypeFilterGroup.filters[0].value = ['image'];
					this.filterSetService.setFilterSet(filterSet);
				}
			} else {
				if (mediatypeFilterGroup) {
					this.filterSetService.removeFilter(mediatypeFilterGroup.filters[0]);
					mediatypeFilterGroup.readOnly = false;
					this.filterSetService.setFilterSet(filterSet);
				}
			}
		}
	}

	onSlideDurationChange() {
		this.onPlaylistFilterChange.emit(this.playlist);
	}

	onFilterClick(filterType: string) {
		this.playlist.filterType = filterType;
	}

	onUseLimitChange(val: Event) {
		if (val && this.playlist.limit !== 30) {
			this.playlist.limit = 30;
		} else if (!val && this.playlist.limit > -1) {
			this.playlist.limit = -1;
		}
		this.onPlaylistFilterChange.emit(this.playlist);
	}

	onQueryLimitChange(limit: number) {
		this.playlist.limit = limit;
		this.onPlaylistFilterChange.emit(this.playlist);
	}

	onFilterChange(filters: any) {
		this.playlist.filters = filters;
		this.onPlaylistFilterChange.emit(this.playlist);
	}

	onQueryChange(query: any) {
		this.playlist.filters = null;
		this.playlist.query = query;
		this.onPlaylistQueryChange.emit(this.playlist);
	}

	updateSort() {
		this.playlist.sort = this.filterSort.property + ' ' + this.filterSort.direction;
		this.sortOrderChange.emit({ property: this.filterSort.property, direction: this.filterSort.direction });
	}

	addClipsToPlaylist(rowsToAdd: TableRow[]) {
		const selectedClips = [];
		for (let i = 0; i < rowsToAdd.length; i++) {
			selectedClips.push(rowsToAdd[i]);
		}
		this.addedSelectedClips.emit(selectedClips);
	}

	linkItem(rowToLink: TableRow) {
		const cliptoLink = [];
		cliptoLink.push(rowToLink);
		this.addedSelectedClips.emit(cliptoLink);
	}

	showPreview(content: Mediaclip | Project) {
		const modal: ModalDefinition = {
			icon: 'film',
			title: (content.title || content.originalfilename),
			component: {
				name: 'playlist-preview',
				inputs: {
					[content.type === 'Project' ? 'project' : 'mediaclip']: content,
					configuration: {
						autoPlay: true,
						showVideo: true,
						alwaysShow: true
					}
				}
			}
		};

		this.overlayService.createModal(modal);
	}
}
